/**
 * Button component — Karr Design System.
 *
 * Follows the "Calm Utility" aesthetic. Supports primary, secondary,
 * ghost, and danger variants in sm/md/lg sizes.
 *
 * @see {@link file:///C:/Users/User/.gemini/antigravity/brain/70517c03-757c-4c92-92d5-477447cbe07e/ui_ux_design.md | UI/UX Design System}
 * @since 1.0.0
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

/** Available button visual variants */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

/** Available button sizes */
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    /** Visual variant of the button */
    variant?: ButtonVariant;
    /** Size of the button */
    size?: ButtonSize;
    /** Show loading spinner and disable button */
    loading?: boolean;
    /** Icon to render before children */
    icon?: ReactNode;
    /** Optional element to render after children */
    suffix?: ReactNode;
}

/*
 * shrinkhead, 2 Oct 2026 — founder: *"make these buttons look more beautiful
 * and attractive all of them"*. Still solid colours, never a gradient: a
 * caller's `className="bg-teal-600"` must keep winning, and a background-image
 * would paint over it. What changed is depth and response: a soft resting
 * shadow, a teal lift on hover, a teal icon on the quiet variants, and a
 * visible keyboard ring.
 */
const variantClasses: Record<ButtonVariant, string> = {
    primary:
        'bg-stone-900 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.25)] hover:bg-stone-800 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_6px_14px_-6px_rgba(28,25,23,0.5)] active:bg-stone-700 active:scale-[0.98] dark:bg-stone-50 dark:text-stone-900 dark:hover:bg-stone-200',
    secondary:
        'bg-white text-stone-700 border border-stone-200 shadow-[0_1px_2px_rgba(28,25,23,0.06)] hover:border-teal-300 hover:bg-teal-50/60 hover:text-stone-900 hover:shadow-[0_6px_14px_-8px_rgba(13,148,136,0.45)] active:bg-teal-50 active:scale-[0.98] dark:bg-stone-800 dark:text-stone-200 dark:border-stone-700 dark:hover:bg-stone-700 dark:hover:border-teal-700 [&>.karr-btn-icon]:text-teal-600 dark:[&>.karr-btn-icon]:text-teal-400',
    ghost:
        'bg-transparent text-stone-600 hover:bg-stone-100 hover:text-stone-900 active:bg-stone-200 active:scale-[0.98] dark:text-stone-400 dark:hover:bg-stone-800 dark:hover:text-stone-100 [&>.karr-btn-icon]:text-teal-600 dark:[&>.karr-btn-icon]:text-teal-400',
    danger:
        'bg-red-700 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(127,29,29,0.3)] hover:bg-red-800 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_6px_14px_-6px_rgba(185,28,28,0.5)] active:bg-red-900 active:scale-[0.98] dark:bg-red-600 dark:hover:bg-red-700',
};

const sizeClasses: Record<ButtonSize, string> = {
    sm: 'h-8 px-3 text-xs gap-1.5 rounded-lg',
    md: 'h-10 px-4 text-sm gap-2 rounded-xl',
    lg: 'h-12 px-6 text-base gap-2.5 rounded-xl',
};

/**
 * Primary interactive button element for the Karr design system.
 *
 * @param props - Button props including variant, size, loading state
 * @returns A styled button element
 *
 * @example
 * ```tsx
 * <Button variant="primary" onClick={save}>Save Invoice</Button>
 * <Button variant="danger" loading={isDeleting}>Delete</Button>
 * <Button variant="ghost" icon={<Plus />}>Add Line</Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            variant = 'primary',
            size = 'md',
            loading = false,
            icon,
            suffix,
            children,
            disabled,
            className = '',
            type = 'button',
            ...props
        },
        ref
    ) => {
        return (
            <button
                ref={ref}
                type={type}
                disabled={disabled || loading}
                data-plain-kept="button"
                className={`
          karr-btn inline-flex items-center justify-center font-medium
          transition-all duration-150 ease-out
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-stone-900
          disabled:opacity-50 disabled:cursor-not-allowed
          cursor-pointer select-none
          ${variantClasses[variant]}
          ${sizeClasses[size]}
          ${className}
        `.trim()}
                {...props}
            >
                {loading ? (
                    <span className="karr-btn-icon shrink-0"><Loader2 className="animate-spin" size={size === 'sm' ? 14 : size === 'md' ? 16 : 18} /></span>
                ) : icon ? (
                    <span className="karr-btn-icon shrink-0">{icon}</span>
                ) : null}
                {/* Labels never wrap. A cramped row used to break a button's own
                    text mid-label ("Print / PDF" over three lines in the invoice
                    drawer); the container is what should wrap or re-flow, not the
                    word. */}
                {/* `karr-btn-label`: in the compact page header a labelled
                    button shows its icon and keeps this for a screen reader
                    and a hover tip (globals.css). */}
                {children && <span className="karr-btn-label whitespace-nowrap">{children}</span>}
                {suffix && <span className="shrink-0">{suffix}</span>}
            </button>
        );
    }
);

Button.displayName = 'Button';
